import getUserInfo from "@api/auth/getUserInfo";
import { login } from "@api/auth/login";
import FlagIcon from "@components/common/icons/flag.icon";
import HomeIcon from "@components/common/icons/home.icon";
import { ModalComponent } from "@components/common/modal";
import { useUserContext } from "@contexts/UserContext/index.context";
import useLocation from "@hooks/location.hook";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, IconButton, styled, Typography } from "@mui/material";
import type { TLoginSchema } from "@schemas/Auth";
import { LoginSchema } from "@schemas/Auth";
import * as Sentry from "@sentry/react";
import { revalidateLogic } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import type { HttpError } from "@types_def/common/error.types";
import type { UserRole } from "@types_def/models/user.type";
import { useCallback, useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppForm } from "@/components/Forms";
import { ForgotPassword } from "../ForgotPassword";
import { SetNewPassword } from "../SetNewPassword";

// Styled components to replace SCSS
const LoginContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: theme.palette.primary.main,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const LoginBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.5rem",
  backgroundColor: theme.palette.background.paper,
  width: "40%",
  padding: "2rem",
  borderRadius: "8px",
  color: "#fff",
}));

const Logo = styled("img")({
  width: "30%",
  padding: "0.5rem",
  margin: "0.75rem 0 1rem 0",
});

const ConnectionText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h5.fontSize, // Approximates var(--large-font-size)
  color: theme.palette.primary.main,
  fontWeight: 600,
  textAlign: "center",
  marginBottom: "1rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  lineHeight: "1.2",
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
}));

const FormBox = styled("form")({
  width: "80%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1rem",
});

const LoginButton = styled(Box)({
  width: "100%",
  "& button": {
    width: "100%",
    display: "flex",
    gap: "1rem",
    "& svg": {
      margin: 0,
    },
  },
});

const initialState = {
  needConfirm: false,
  showPassword: false,
  errorMessage: "",
  modalOpen: {
    forgotPassword: false,
    newPassword: false,
  },
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_NEED_CONFIRM":
      return { ...state, needConfirm: action.payload };
    case "TOGGLE_SHOW_PASSWORD":
      return { ...state, showPassword: !state.showPassword };
    case "SET_ERROR_MESSAGE":
      return { ...state, errorMessage: action.payload };
    case "TOGGLE_MODAL":
      return {
        ...state,
        modalOpen: {
          ...state.modalOpen,
          [action.payload]: !state.modalOpen[action.payload],
        },
      };
    default:
      return state;
  }
}

function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleSetUser, userState } = useUserContext();
  const location = useLocation();
  const [state, dispatch] = useReducer(reducer, initialState);

  const handleRedirect = useCallback(
    (userRole: UserRole) => {
      const pathName = location?.from?.pathname || `/${userRole.toLowerCase()}`;
      navigate(pathName);
    },
    [location?.from?.pathname, navigate]
  );

  useEffect(() => {
    if (userState?.["custom:role"]) {
      handleRedirect(userState["custom:role"]);
    }
  }, [userState, handleRedirect]);

  const setUserInfo = async () => {
    const { success, data } = await getUserInfo();
    if (success && data) {
      handleSetUser({ ...data, connected: true });
      Sentry.setUser({
        id: data.sub,
        role: data["custom:role"],
      });
    }
  };

  const form = useAppForm({
    defaultValues: {
      username: "",
      password: "",
      code: undefined,
      session: undefined,
    } as TLoginSchema,
    validationLogic: revalidateLogic(),
    validators: {
      onChange: LoginSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const { username, password } = value;
        const res = await login({ username, password });
        if (res.success) {
          if (res.action === "LOGIN") {
            await setUserInfo();
            return;
          }
          if (res.action === "NEW_PASSWORD_REQUIRED") {
            form.setFieldValue("session", res.session as string);
            dispatch({ type: "TOGGLE_MODAL", payload: "newPassword" });
          }
          if (res.action === "need-confirm") {
            dispatch({ type: "SET_NEED_CONFIRM", payload: true });
            dispatch({
              type: "SET_ERROR_MESSAGE",
              payload: "Votre compte n'est pas encore confirmé",
            });
          }
        } else {
          dispatch({ type: "SET_ERROR_MESSAGE", payload: "Email ou mot de passe incorrect" });
        }
      } catch (error) {
        dispatch({ type: "SET_ERROR_MESSAGE", payload: (error as HttpError).message as string });
      }
    },
  });

  const toggleShowPassword = () => dispatch({ type: "TOGGLE_SHOW_PASSWORD" });

  const handleModal = (modal: "forgotPassword" | "newPassword") => {
    dispatch({ type: "TOGGLE_MODAL", payload: modal });
  };

  return (
    <>
      <LoginContainer>
        <LoginBox>
          <Logo src="/images/logo.png" alt="logo" />
          <ConnectionText color="primary.main">{t("login.welcome")}</ConnectionText>
          <Typography
            sx={{
              color: "secondary.main",
              fontSize: "0.8rem",
            }}
          >
            {state.errorMessage}
          </Typography>
          <form.AppForm>
            <FormBox
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
              }}
            >
              <form.AppField name="username">
                {(f) => (
                  <f.AlbInput label={t("login.identifier")} type="TextField" variant="filled" />
                )}
              </form.AppField>
              <form.AppField name="password">
                {(f) => (
                  <f.AlbInput
                    label={t("common.form.input.password")}
                    type="TextField"
                    variant="standard"
                    inputType={state.showPassword ? "text" : "password"}
                    stripSpaces
                    inputProps={{
                      endAdornment: (
                        <IconButton onClick={toggleShowPassword} edge="end">
                          {state.showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                )}
              </form.AppField>
              {state.needConfirm && (
                <form.AppField name="code">
                  {(f) => (
                    <f.AlbInput
                      label={t("common.form.input.confirmation-code")}
                      type="TextField"
                      variant="standard"
                    />
                  )}
                </form.AppField>
              )}
              <LoginButton>
                <Button color="secondary" variant="contained" type="submit" fullWidth>
                  <HomeIcon />
                  {t("login.connect")}
                  <FlagIcon />
                </Button>
              </LoginButton>
            </FormBox>
          </form.AppForm>
          <Box
            sx={{
              width: "80%",
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "1rem",
            }}
          >
            <Button
              onClick={() => handleModal("forgotPassword")}
              color="secondary"
              variant="text"
              sx={{
                textTransform: "capitalize",
                fontSize: "0.8rem",
                fontWeight: 600,
                textDecoration: "underline",
                textAlign: "right",
              }}
            >
              {t("login.forget-password")} ?
            </Button>
          </Box>
        </LoginBox>
      </LoginContainer>
      <ModalComponent
        handleClose={() => handleModal("forgotPassword")}
        isOpen={state.modalOpen.forgotPassword}
        showButton={false}
      >
        <ForgotPassword closeModal={() => handleModal("forgotPassword")} />
      </ModalComponent>
      <form.Subscribe
        selector={(s) => ({
          username: s.values.username,
          session: s.values.session,
        })}
      >
        {({ username, session }) => (
          <ModalComponent
            handleClose={() => handleModal("newPassword")}
            isOpen={state.modalOpen.newPassword}
            showButton={false}
          >
            <SetNewPassword email={username ?? ""} session={session ?? ""} />
          </ModalComponent>
        )}
      </form.Subscribe>
    </>
  );
}

export default Login;
